import { Frame } from "lucide-react";
import { PREVIEW_MODES, PREVIEW_MODE_COPY } from "@/lib/canvasConfig";

const ROOM_BG =
  "https://images.unsplash.com/photo-1758448755778-90ebf4d0f1e7?w=1200&q=60";

/**
 * Left column of the canvas product detail page: main preview, preview-mode
 * tabs (Artwork / Framed / In Room), and thumbnail strip.
 *
 * All state (activeImg, previewMode, selectedFrame) lives in the parent page;
 * this component only renders and forwards click handlers.
 */
export default function CanvasPreviewPane({
  product,
  images,
  activeImg,
  setActiveImg,
  previewMode,
  setPreviewMode,
  selectedFrame,
  setSelectedFrame,
  onOpenLightbox,
  desc,
  frameStyle,
}) {
  const hasImage = images.length > 0;
  const currentImg = images[activeImg] || images[0];

  return (
    <div>
      {/* main preview */}
      <button
        type="button"
        onClick={() => hasImage && onOpenLightbox(activeImg)}
        className={`relative block w-full overflow-hidden rounded-sm ${
          previewMode === "room" ? "aspect-[4/3]" : "aspect-square"
        } bg-brand-bg ${hasImage ? "cursor-zoom-in" : "cursor-default"}`}
        data-testid="product-preview"
      >
        {hasImage ? (
          previewMode === "room" ? (
            /* ── IN ROOM ── */
            <div className="w-full h-full relative">
              <img
                src={ROOM_BG}
                alt=""
                className="w-full h-full object-cover brightness-[0.35]"
              />
              <div className="absolute inset-0 flex items-center justify-center p-12">
                <div className="relative max-w-[55%] shadow-[0_8px_40px_rgba(0,0,0,0.6)]">
                  <img
                    src={currentImg.url}
                    alt={product.title}
                    className={`w-full h-auto object-contain ${frameStyle()}`}
                  />
                </div>
              </div>
            </div>
          ) : (
            /* ── ARTWORK / FRAMED ── */
            <div className="w-full h-full flex items-center justify-center p-8 md:p-12 bg-gradient-to-br from-brand-surface to-brand-bg">
              <img
                src={currentImg.url}
                alt={product.title}
                className={`max-w-full max-h-full object-contain transition-[border-color,color,background-color] duration-300 ${frameStyle()}`}
              />
            </div>
          )
        ) : (
          /* ── NO IMAGE — editorial layout ── */
          <div className="w-full h-full flex flex-col justify-end bg-gradient-to-b from-brand-surface via-brand-surface to-brand-bg px-8 md:px-12 pb-10 pt-12 relative">
            <div className="absolute inset-4 border border-brand-border/15 rounded-[1px]" />
            <p className="text-[11px] tracking-[0.2em] uppercase text-brand-gold/50 font-semibold mb-4 relative">
              {product.productType || "Canvas Print"}
            </p>
            {desc && (
              <p className="relative text-[15px] md:text-[17px] text-brand-text-secondary/40 leading-relaxed max-w-sm">
                {desc.slice(0, 250)}
                {desc.length > 250 ? "\u2026" : ""}
              </p>
            )}
            {!desc && (
              <Frame size={32} strokeWidth={1} className="text-brand-border/20 relative" />
            )}
          </div>
        )}
      </button>

      {/* preview mode tabs */}
      {hasImage && (
        <>
          <div
            className="mt-3 flex gap-1 bg-brand-surface/60 rounded-sm p-1"
            data-testid="preview-mode-switcher"
          >
            {PREVIEW_MODES.map((m) => {
              const active = previewMode === m.id;
              const cls = active
                ? "bg-brand-bg text-brand-gold shadow-sm"
                : "text-brand-text-secondary/60 hover:text-brand-text-secondary";
              return (
                <button
                  key={m.id}
                  onClick={() => {
                    if (m.id === "framed" && selectedFrame === "none") {
                      setSelectedFrame("black");
                    }
                    setPreviewMode(m.id);
                  }}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-sm text-[11px] tracking-wide font-medium transition-[border-color,color,background-color] duration-200 ${cls}`}
                  data-testid={`preview-mode-${m.id}`}
                >
                  <m.icon size={13} strokeWidth={1.6} />
                  {m.label}
                </button>
              );
            })}
          </div>
          <p
            className="mt-2 text-[12px] text-brand-text-secondary/55 leading-relaxed"
            data-testid="preview-mode-helper"
          >
            {PREVIEW_MODE_COPY[previewMode]}
          </p>
        </>
      )}

      {/* image thumbnails */}
      {images.length > 1 && (
        <div className="mt-3 flex gap-2">
          {images.map((img, i) => {
            const cls = i === activeImg
              ? "border-brand-gold"
              : "border-brand-border hover:border-brand-gold/40";
            return (
              <button
                key={img.url || `thumb-${i}`}
                onClick={() => setActiveImg(i)}
                className={`w-14 h-14 rounded-sm overflow-hidden border transition-colors duration-200 ${cls}`}
                data-testid={`thumbnail-${i}`}
              >
                <img src={img.url} alt="" className="w-full h-full object-cover" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
